import { Box, Typography } from '@mui/material';

export function DiagramBuilding({
  variant = 'assembling',
}: {
  variant?: 'flowing' | 'assembling';
}) {
  return (
    <Box
      data-variant={variant}
      sx={(theme) => ({
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        overflow: 'auto',
        p: 3,
        '& [data-part="connector"]': {
          fill: 'none',
          stroke: theme.palette.dataVisualizationPrimary.light,
          strokeWidth: 1.5,
        },
        '& [data-part="signal"]': {
          fill: 'none',
          stroke: theme.palette.dataVisualizationSecondary.main,
          strokeWidth: 2.5,
          strokeLinecap: 'round',
          strokeDasharray: '12 100',
          strokeDashoffset: 12,
          opacity: 0,
          animation: 'diagram-travel 6s ease-in-out infinite',
        },
        '& [data-part="card"]': {
          fill: theme.palette.dataVisualizationPrimary.ultraLight,
          stroke: theme.palette.dataVisualizationPrimary.light,
        },
        '& [data-part="halo"]': {
          fill: theme.palette.dataVisualizationSecondary.main,
          opacity: 0,
          transformBox: 'fill-box',
          transformOrigin: 'center',
          animation: 'diagram-glow 6s ease-out infinite',
        },
        '& [data-part="dot"]': {
          fill: theme.palette.dataVisualizationPrimary.light,
        },
        '& [data-part="copy"]': {
          stroke: theme.palette.dataVisualizationPrimary.light,
          strokeWidth: 4,
          strokeLinecap: 'round',
        },
        '& [data-part="highlight"]': {
          fill: 'none',
          stroke: theme.palette.dataVisualizationSecondary.main,
          strokeWidth: 1.5,
          opacity: 0,
          animation: 'diagram-illuminate 6s ease-in-out infinite',
        },
        '& [data-part="junction"]': {
          fill: theme.palette.dataVisualizationSecondary.main,
        },
        '&[data-variant="assembling"] [data-part="piece"]': {
          '--drift-x': '0px',
          '--drift-y': '-12px',
          '--tilt': '-3deg',
          transformBox: 'fill-box',
          transformOrigin: 'center',
          animation: 'diagram-assemble 8s ease-in-out infinite',
        },
        '&[data-variant="assembling"] [data-piece="1"]': {
          '--drift-x': '-14px',
          '--drift-y': '10px',
          '--tilt': '4deg',
          animationDelay: '-7.4s',
        },
        '&[data-variant="assembling"] [data-piece="2"]': {
          '--drift-x': '14px',
          '--drift-y': '14px',
          '--tilt': '-4deg',
          animationDelay: '-6.8s',
        },
        '&[data-variant="assembling"] [data-part="connector"]': {
          strokeDasharray: 100,
          animation: 'diagram-sketch 8s ease-in-out infinite',
        },
        '&[data-variant="assembling"] [data-part="signal"], &[data-variant="assembling"] [data-part="junction"]':
          { display: 'none' },
        '&[data-variant="assembling"] [data-part="highlight"], &[data-variant="assembling"] [data-part="halo"]':
          { animationDuration: '8s' },
        '@keyframes diagram-travel': {
          '0%, 14%': { strokeDashoffset: 12, opacity: 0 },
          '18%': { opacity: 1 },
          '54%': { strokeDashoffset: -100, opacity: 1 },
          '60%, 100%': { strokeDashoffset: -100, opacity: 0 },
        },
        '@keyframes diagram-glow': {
          '0%, 45%, 100%': { opacity: 0, transform: 'scale(1)' },
          '12%': { opacity: 0.14, transform: 'scale(1.03)' },
          '35%': { opacity: 0, transform: 'scale(1.12)' },
        },
        '@keyframes diagram-illuminate': {
          '0%, 40%, 100%': { opacity: 0 },
          '10%, 20%': { opacity: 1 },
        },
        '@keyframes diagram-assemble': {
          '0%, 100%': {
            opacity: 0.12,
            transform:
              'translate(var(--drift-x), var(--drift-y)) rotate(var(--tilt))',
            filter: 'blur(1px)',
          },
          '32%, 68%': {
            opacity: 1,
            transform: 'translate(0, 0) rotate(0deg)',
            filter: 'blur(0)',
          },
        },
        '@keyframes diagram-sketch': {
          '0%, 28%, 100%': { strokeDashoffset: 100, opacity: 0 },
          '55%, 72%': { strokeDashoffset: 0, opacity: 1 },
          '88%': { strokeDashoffset: 0, opacity: 0 },
        },
        '@media (prefers-reduced-motion: reduce)': {
          '& [data-part="piece"], & [data-part="connector"], & [data-part="highlight"], & [data-part="signal"], & [data-part="halo"]':
            { animation: 'none' },
        },
      })}
    >
      <Box sx={{ width: '100%', maxWidth: 360, textAlign: 'center' }}>
        <Box
          component="svg"
          viewBox="0 0 320 190"
          aria-hidden="true"
          focusable="false"
          sx={{
            display: 'block',
            width: '100%',
            maxWidth: 320,
            maxHeight: '28vh',
            mx: 'auto',
            mb: 3.5,
            overflow: 'visible',
          }}
        >
          <path
            data-part="connector"
            pathLength={100}
            d="M160 67V93Q160 105 148 105H86Q74 105 74 117V127M160 93Q160 105 172 105H234Q246 105 246 117V127"
          />
          {[
            'M160 67V93Q160 105 148 105H86Q74 105 74 117V127',
            'M160 67V93Q160 105 172 105H234Q246 105 246 117V127',
          ].map((path) => (
            <path key={path} data-part="signal" d={path} pathLength={100} />
          ))}
          {[
            { x: 104, y: 15, delay: '0s' },
            { x: 18, y: 127, delay: '2.4s' },
            { x: 190, y: 127, delay: '2.4s' },
          ].map(({ x, y, delay }, index) => (
            <g key={x} transform={`translate(${x} ${y})`}>
              <g data-part="piece" data-piece={index}>
                <rect
                  data-part="halo"
                  style={{ animationDelay: delay }}
                  x="-5"
                  y="-5"
                  width="122"
                  height="62"
                  rx="14"
                />
                <rect data-part="card" width="112" height="52" rx="9" />
                <circle data-part="dot" cx="22" cy="26" r="8" />
                <path data-part="copy" d="M42 22H91M42 32H77" />
                <rect
                  data-part="highlight"
                  style={{ animationDelay: delay }}
                  width="112"
                  height="52"
                  rx="9"
                />
              </g>
            </g>
          ))}
          <circle data-part="junction" cx="160" cy="105" r="4" />
        </Box>
        <div role="status" aria-live="polite" aria-atomic="true">
          <Typography variant="title-m" component="h2">
            Creating your diagram
          </Typography>
          <Typography variant="body-s" sx={{ mt: 1 }}>
            Your diagram will appear here when it is ready.
          </Typography>
        </div>
      </Box>
    </Box>
  );
}
